import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  variant?: "default" | "white" | "compact";
  size?: "sm" | "md" | "lg" | "xl";
}

export const Logo = ({ className, variant = "default", size = "md" }: LogoProps) => {
  const sizeClasses = {
    sm: "h-16", // Increased from h-8 to h-16 (2x bigger)
    md: "h-20", // Increased from h-12 to h-20
    lg: "h-24", // Increased from h-16 to h-24
    xl: "h-32", // Increased from h-24 to h-32
  };

  return (
    <div className={cn("flex items-center logo-container", className)}>
      <img
        src="/aaa-logo.png"
        alt="AAAprinters Logo"
        className={cn(
          sizeClasses[size],
          "w-auto object-contain logo-animated",
          variant === "white" && "brightness-0 invert"
        )}
      />
      <style>{`
        /* 3D Perspective Container */
        .logo-container {
          perspective: 1000px;
          transform-style: preserve-3d;
        }

        /* Animated Logo with Multiple Effects */
        .logo-animated {
          transform-style: preserve-3d;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          filter: drop-shadow(0 2px 8px rgba(0, 174, 239, 0.2))
                  drop-shadow(0 2px 8px rgba(236, 0, 140, 0.2))
                  drop-shadow(0 2px 8px rgba(255, 242, 0, 0.2));
          animation: logo-entrance 1s ease-out;
        }

        /* Entrance Animation - Plays on load */
        @keyframes logo-entrance {
          0% {
            opacity: 0;
            transform: translateY(-20px) rotateY(-90deg) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateY(0) rotateY(0deg) scale(1);
          }
        }

        /* Hover: 3D Flip + Scale + Enhanced Glow */
        .logo-animated:hover {
          transform: rotateY(360deg) scale(1.15);
          filter: drop-shadow(0 4px 20px rgba(0, 174, 239, 0.6))
                  drop-shadow(0 4px 20px rgba(236, 0, 140, 0.6))
                  drop-shadow(0 4px 20px rgba(255, 242, 0, 0.6));
        }

        /* Active/Click State - Reverse Flip */
        .logo-animated:active {
          transform: rotateY(-180deg) scale(1.05);
          transition: all 0.3s ease;
        }

        /* Continuous Subtle Pulse (when not hovering) */
        @keyframes logo-pulse {
          0%, 100% {
            filter: drop-shadow(0 2px 8px rgba(0, 174, 239, 0.2))
                    drop-shadow(0 2px 8px rgba(236, 0, 140, 0.2))
                    drop-shadow(0 2px 8px rgba(255, 242, 0, 0.2));
          }
          50% {
            filter: drop-shadow(0 3px 12px rgba(0, 174, 239, 0.4))
                    drop-shadow(0 3px 12px rgba(236, 0, 140, 0.4))
                    drop-shadow(0 3px 12px rgba(255, 242, 0, 0.4));
          }
        }

        /* Apply pulse animation */
        .logo-container:not(:hover) .logo-animated {
          animation: logo-pulse 3s ease-in-out infinite;
        }

        /* Mobile Optimization - Smaller size and faster animation */
        @media (max-width: 768px) {
          .logo-animated {
            transition: all 0.4s ease;
          }

          .logo-animated:hover {
            transform: rotateY(180deg) scale(1.1);
          }
        }

        /* Reduced Motion Support */
        @media (prefers-reduced-motion: reduce) {
          .logo-animated {
            animation: none;
            transition: filter 0.3s ease;
          }

          .logo-animated:hover {
            transform: scale(1.1);
          }
        }
      `}</style>
    </div>
  );
};

// Export a simple text version for use in places where styling is limited
export const LogoText = () => "AAAprinters";
